import { uiPaper } from "shared/ui/uiSurface"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material"
import { FC, useState } from "react"
import { useDebugTheme } from "../hooks/useDebugTheme"
import { SIDEBAR_STYLE_OPTIONS, useSidebarStyle, type SidebarStyleKey } from "../hooks/useSidebarStyle"
import { UI_DENSITY_OPTIONS, useUiDensity, type UiDensity } from "../hooks/useUiDensity"

const PRESETS = [
  { label: "White (default)", value: "#ffffff" },
  { label: "Light blue tint", value: "#f5f7ff" },
  { label: "Match sidebar (dark navy)", value: "#1e2235" },
  { label: "Lighter navy", value: "#262b42" },
  { label: "Blue", value: "#3591d8" },
] as const

interface DebugDialogProps {
  open: boolean
  onClose: () => void
  appbarColor: string
  onColorChange: (hex: string) => void
}

export const DebugDialog: FC<DebugDialogProps> = ({
  open,
  onClose,
  appbarColor,
  onColorChange,
}) => {
  const { customTheme, setCustomTheme } = useDebugTheme()
  const { density, setDensity } = useUiDensity()
  const { style: sidebarStyle, setStyle: setSidebarStyle } = useSidebarStyle()
  const isCustom = !PRESETS.some((p) => p.value === appbarColor)
  const [customMode, setCustomMode] = useState(isCustom)
  const [customHex, setCustomHex] = useState(isCustom ? appbarColor : "")

  const selectValue = customMode ? "custom" : appbarColor

  const handleSelectChange = (value: string) => {
    if (value === "custom") {
      setCustomMode(true)
    } else {
      setCustomMode(false)
      onColorChange(value)
    }
  }

  const handleCustomInput = (value: string) => {
    setCustomHex(value)
    if (/^#[0-9a-fA-F]{6}$/.test(value)) {
      onColorChange(value)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: { sx: { backgroundColor: "transparent" } },
        paper: { ...uiPaper("UI-DLV2-011"), sx: { width: 360 } },
      }}
    >
      <DialogTitle>Debug Settings</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={customTheme}
              onChange={(e) => setCustomTheme(e.target.checked)}
            />
          }
          label="Mobilus Theme"
          sx={{ mt: 1, mb: 1 }}
        />

        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
          <InputLabel>UI Density</InputLabel>
          <Select
            value={density}
            label="UI Density"
            onChange={(e) => setDensity(e.target.value as UiDensity)}
          >
            {UI_DENSITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
          <InputLabel>Sidebar Style</InputLabel>
          <Select
            value={sidebarStyle}
            label="Sidebar Style"
            onChange={(e) => setSidebarStyle(e.target.value as SidebarStyleKey)}
          >
            {SIDEBAR_STYLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        <FormControl fullWidth size="small">
          <InputLabel>Appbar Color</InputLabel>
          <Select
            value={selectValue}
            label="Appbar Color"
            onChange={(e) => handleSelectChange(e.target.value)}
          >
            {PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
            <MenuItem value="custom">Custom...</MenuItem>
          </Select>
        </FormControl>

        {customMode && (
          <TextField
            fullWidth
            size="small"
            label="Hex color"
            placeholder="#1e2235"
            value={customHex}
            onChange={(e) => handleCustomInput(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Enter a 6-digit hex color (e.g. #1e2235)"
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
